import React, { useState, useEffect } from 'react';
import { supabase_get_client_settings, supabase_update_client_settings, extractErrorMessage } from '../services/supabaseService';
import { AI_PROXY_CHAT_MODEL } from '../services/geminiService';
import { AiMatchingSettings } from '../types';
import { Save, Loader2, Sliders, BrainCircuit as Brain, RotateCcw, AlertTriangle, AlertCircle, CheckCircle2 } from 'lucide-react';

interface AiMatchingSettingsTabProps {
    clientId: string;
}

export const AiMatchingSettingsTab: React.FC<AiMatchingSettingsTabProps> = ({ clientId }) => {
    const [aiSettings, setAiSettings] = useState<AiMatchingSettings>({
        customPrompt: '',
        rejectionReasons: ['Kandidaat teruggetrokken', 'Afgewezen door opdrachtgever', 'Opdracht ingetrokken'],
        weights: { hardSkills: 40, softSkills: 30, experience: 20, culturalFit: 10 },
        model: AI_PROXY_CHAT_MODEL
    });
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    useEffect(() => {
        if (clientId) {
            fetchSettings();
        }
    }, [clientId]);

    const fetchSettings = async () => {
        setIsLoading(true);
        try {
            const settings = await supabase_get_client_settings(clientId);
            if (settings && settings.ai_matching_settings) {
                // Merge with defaults in case of missing keys
                setAiSettings(prev => ({
                    ...prev,
                    ...settings.ai_matching_settings,
                    weights: {
                        ...prev.weights,
                        ...(settings.ai_matching_settings.weights || {})
                    }
                }));
            }
        } catch (e: any) {
            console.error('Error fetching AI settings:', e.message);
        } finally {
            setIsLoading(false);
        }
    };

    const [isGeneratingEmbeddings, setIsGeneratingEmbeddings] = useState(false);
    const [embeddingProgress, setEmbeddingProgress] = useState<{current: number, total: number} | null>(null);
    const [forceRegenerate, setForceRegenerate] = useState(false);
    /** Bij herdruk: sla kandidaten over die embedding_updated_at al vandaag (UTC) hebben */
    const [skipEmbeddingsUpdatedToday, setSkipEmbeddingsUpdatedToday] = useState(true);

    const startOfUtcTodayIso = () => {
        const d = new Date();
        d.setUTCHours(0, 0, 0, 0);
        return d.toISOString();
    };

    const buildEmbeddingText = (c: any) => {
        const ai = c.ai_parsed_data && typeof c.ai_parsed_data === 'object' ? c.ai_parsed_data : {};
        let t = `Naam: ${c.name || 'Onbekend'}\n`;
        t += `Rol: ${c.experience || c.role || ai.currentRole || ai.role || ''}\n`;
        if (c.cv_summary) t += `Profiel: ${String(c.cv_summary).slice(0, 2000)}\n`;
        if (ai.summary || ai.Summary) t += `Samenvatting: ${String(ai.summary || ai.Summary).slice(0, 1500)}\n`;
        const tags = ai.tags || ai['Key Skills'];
        if (tags) {
            const tagStr = Array.isArray(tags)
                ? tags.map((x: any) => (typeof x === 'string' ? x : x.name)).filter(Boolean).join(', ')
                : String(tags);
            if (tagStr) t += `Skills: ${tagStr.slice(0, 1500)}\n`;
        }
        if (Array.isArray(ai.timeline) && ai.timeline.length) {
            const jobs = ai.timeline
                .slice(0, 6)
                .map((x: any) => `${x.role || x.title || ''} bij ${x.company || ''} (${x.period || x.year || ''})`)
                .join(' | ');
            t += `Werkervaring: ${jobs}\n`;
        }
        if (c.original_cv_text) {
            t += `CV-tekst: ${String(c.original_cv_text).replace(/\s+/g, ' ').trim().slice(0, 4500)}\n`;
        }
        return t.slice(0, 9500);
    };

    const handleGenerateMissingEmbeddings = async () => {
        setIsGeneratingEmbeddings(true);
        setError(null);
        setSuccess(null);
        
        try {
            const { supabase } = await import('../services/supabaseService');
            const { generateEmbedding } = await import('../services/geminiService');

            // 1. Haal Uitsluitend profielen op voor deze tenant (met paginatie voor >1000 records)
            let allCandidates: any[] = [];
            let fetchStart = 0;
            const pageSize = 1000;
            
            const todayStart = startOfUtcTodayIso();

            while (true) {
                let query = supabase
                    .from('candidates')
                    .select('id, cv_summary, original_cv_text, name, experience, role, ai_parsed_data')
                    .eq('client_id', clientId)
                    .order('id')
                    .range(fetchStart, fetchStart + pageSize - 1);

                if (!forceRegenerate) {
                    query = query.is('embedding_updated_at', null);
                } else if (skipEmbeddingsUpdatedToday) {
                    query = query.or(`embedding_updated_at.is.null,embedding_updated_at.lt.${todayStart}`);
                }
                    
                const { data: batch, error: candError } = await query;
                    
                if (candError) throw candError;
                if (!batch || batch.length === 0) break;
                
                allCandidates = allCandidates.concat(batch);
                
                if (batch.length < pageSize) break;
                fetchStart += pageSize;
            }
            
            const candidates = allCandidates;
            
            if (!candidates || candidates.length === 0) {
                setSuccess("Fantastisch! Alle profielen zijn reeds voorzien van een AI Vector Embedding.");
                setIsGeneratingEmbeddings(false);
                return;
            }
            
            setEmbeddingProgress({ current: 0, total: candidates.length });

            const concurrency = 6;
            const delayBetweenBatchesMs = 350;
            let processed = 0;

            for (let i = 0; i < candidates.length; i += concurrency) {
                const wave = candidates.slice(i, i + concurrency);
                await Promise.all(
                    wave.map(async (cand) => {
                        const textToEmbed = buildEmbeddingText(cand);
                        if (!textToEmbed.trim()) return;
                        try {
                            const embed = await generateEmbedding(textToEmbed);
                            if (embed) {
                                await supabase.from('candidates').update({
                                    embedding: embed,
                                    embedding_updated_at: new Date().toISOString(),
                                }).eq('id', cand.id);
                            }
                        } catch {
                            /* een mislukte rij overslaan */
                        }
                    })
                );
                processed += wave.length;
                setEmbeddingProgress({ current: processed, total: candidates.length });
                if (delayBetweenBatchesMs > 0 && i + concurrency < candidates.length) {
                    await new Promise((r) => setTimeout(r, delayBetweenBatchesMs));
                }
            }
            
            setSuccess(`${processed} AI Vector Embeddings succesvol gegenereerd en gesynchroniseerd!`);
        } catch (e: any) {
            setError(extractErrorMessage(e));
        } finally {
            setIsGeneratingEmbeddings(false);
            setEmbeddingProgress(null);
        }
    };

    const handleSaveAiSettings = async () => {
        if (!clientId) return;
        setIsSaving(true);
        setError(null);
        setSuccess(null);
        try {
            await supabase_update_client_settings(clientId, aiSettings);
            setSuccess("AI Settings Opgeslagen!");
            setTimeout(() => setSuccess(null), 3000);
        } catch (e: any) {
            setError(extractErrorMessage(e));
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return (
            <div className="flex justify-center p-10">
                <Loader2 className="animate-spin text-slate-400" size={32} />
            </div>
        );
    }

    return (
        <div className="max-w-4xl space-y-8 animate-fadeIn">
            {error && <div className="bg-red-50 border border-red-100 text-red-600 p-4 rounded-xl flex items-center gap-3"><AlertCircle size={20} /><span className="text-xs font-bold">{error}</span></div>}
            {success && <div className="bg-emerald-50 border border-emerald-100 text-emerald-600 p-4 rounded-xl flex items-center gap-3"><CheckCircle2 size={20} /><span className="text-xs font-bold">{success}</span></div>}

            <div className="flex items-center justify-between pb-6 border-b border-slate-200">
                <div>
                    <h2 className="text-xl font-black text-slate-900 tracking-tight">AI Matching Configuratie</h2>
                    <p className="text-sm text-slate-500 font-medium mt-1">
                        Beheer hoe AI kandidaten matcht en scoort bij het zoeken en vergelijken in jouw talentpool.
                    </p>
                </div>
                <button
                    onClick={handleSaveAiSettings}
                    disabled={isSaving}
                    className="px-6 py-2.5 bg-slate-900 text-white text-xs font-bold uppercase tracking-widest rounded-lg hover:bg-black transition-colors flex items-center gap-2 shadow-md shadow-slate-200"
                >
                    {isSaving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Instellingen Opslaan
                </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                {/* LEFT: SLIDERS & CONFIG */}
                <div className="space-y-8">
                    <div className="bg-slate-50 p-6 rounded-2xl border border-slate-100">
                        <h4 className="flex items-center gap-2 text-sm font-black uppercase tracking-widest text-slate-900 mb-6">
                            <Sliders size={16} /> Workflow Statussen
                        </h4>
                        <div className="space-y-4">
                            <p className="text-xs font-bold text-slate-700">Afwijzingsredenen (Rejection Reasons)</p>
                            <p className="text-[10px] text-slate-500 font-medium">Beheer de opties die recruiters kunnen kiezen wanneer een kandidaat wordt afgewezen (komma-gescheiden).</p>
                            <input
                                type="text"
                                value={aiSettings.rejectionReasons?.join(', ') || ''}
                                onChange={(e) => {
                                    const reasons = e.target.value.split(',').map(r => r.trim()).filter(Boolean);
                                    setAiSettings({ ...aiSettings, rejectionReasons: reasons.length > 0 ? reasons : [] });
                                }}
                                placeholder="Bijv: Kandidaat teruggetrokken, Geen match..."
                                className="w-full p-3 bg-white border border-slate-200 rounded-lg text-sm font-medium outline-none focus:border-slate-400 transition-all"
                            />
                        </div>
                    </div>

                    <div className="bg-slate-50 p-6 rounded-2xl border border-slate-100">
                        <h4 className="flex items-center gap-2 text-sm font-black uppercase tracking-widest text-slate-900 mb-6">
                            <Sliders size={16} /> Wegingsfactoren
                        </h4>
                        <div className="space-y-6">
                            {[
                                { key: 'hardSkills', label: 'Hard Skills & Tools' },
                                { key: 'experience', label: 'Werkervaring & Senioriteit' },
                                { key: 'softSkills', label: 'Soft Skills & Persoonlijkheid' },
                                { key: 'culturalFit', label: 'Culturele Fit & Waarden' }
                            ].map((item) => (
                                <div key={item.key}>
                                    <div className="flex justify-between mb-2">
                                        <label className="text-xs font-bold text-slate-700">{item.label}</label>
                                        <span className="text-xs font-black bg-white px-2 py-0.5 rounded border border-slate-200 text-slate-900">
                                            {(aiSettings.weights as any)[item.key]}%
                                        </span>
                                    </div>
                                    <input
                                        type="range"
                                        min="0"
                                        max="100"
                                        step="5"
                                        value={(aiSettings.weights as any)[item.key]}
                                        onChange={(e) => setAiSettings({
                                            ...aiSettings,
                                            weights: { ...aiSettings.weights, [item.key]: parseInt(e.target.value) }
                                        })}
                                        className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-slate-900 hover:accent-emerald-500 transition-all"
                                    />
                                </div>
                            ))}
                            <div className="pt-4 border-t border-slate-200 mt-4">
                                <div className="flex justify-between items-center">
                                    <span className="text-[10px] font-black uppercase text-slate-400">Totaal</span>
                                    <span className={`text-xs font-black px-3 py-1 rounded-lg ${Object.values(aiSettings.weights).reduce((a: number, b: number) => a + b, 0) === 100
                                        ? 'bg-emerald-100 text-emerald-700'
                                        : 'bg-amber-100 text-amber-700'
                                        }`}>
                                        {Object.values(aiSettings.weights).reduce((a: number, b: number) => a + b, 0)}%
                                    </span>
                                </div>
                                {Object.values(aiSettings.weights).reduce((a: number, b: number) => a + b, 0) !== 100 && (
                                    <p className="text-[10px] text-amber-600 mt-2 font-bold flex items-center gap-1">
                                        <AlertTriangle size={10} /> Totaal moet 100% zijn voor accurate resultaten.
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* RIGHT: PROMPT */}
                <div className="space-y-4">
                    <div className="flex justify-between items-center">
                        <h4 className="flex items-center gap-2 text-sm font-black uppercase tracking-widest text-slate-900">
                            <Brain size={16} /> System Prompt
                        </h4>
                        <button
                            onClick={() => setAiSettings({ ...aiSettings, customPrompt: '' })}
                            className="text-[10px] font-bold text-slate-400 hover:text-red-500 uppercase tracking-wider flex items-center gap-1"
                        >
                            <RotateCcw size={10} /> Reset naar Standaard
                        </button>
                    </div>
                    <div className="relative">
                        <textarea
                            value={aiSettings.customPrompt}
                            onChange={(e) => setAiSettings({ ...aiSettings, customPrompt: e.target.value })}
                            placeholder="Opereer als een kritische recruiter. Focus op stabiliteit en loyaliteit..."
                            className="w-full h-[400px] p-5 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-mono text-slate-700 leading-relaxed outline-none focus:border-slate-400 focus:ring-4 focus:ring-slate-100 transition-all resize-none shadow-inner"
                        />
                        <div className="absolute bottom-4 right-4 text-[10px] font-bold text-slate-400 bg-white/80 px-2 py-1 rounded backdrop-blur-sm">
                            {aiSettings.customPrompt?.length || 0} characters
                        </div>
                    </div>
                    <p className="text-[10px] text-slate-400 leading-relaxed">
                        <span className="font-bold text-slate-900">Tip:</span> Gebruik variabelen of placeholders is niet nodig; dit is de systeeminstructie die de persoonlijkheid en strengheid van de AI bepaalt. De vacature- en kandidaatcontext worden automatisch toegevoegd.
                    </p>
                </div>
            </div>

            {/* VECTOR EMBEDDINGS BACKFILL MANAGER */}
            <div className="bg-slate-900 rounded-2xl p-8 text-white relative overflow-hidden shadow-xl mt-8">
                <div className="absolute -top-24 -right-24 w-64 h-64 bg-indigo-500 rounded-full blur-[80px] opacity-30 pointer-events-none"></div>
                
                <div className="relative z-10 flex flex-col md:flex-row gap-8 items-center justify-between">
                    <div className="space-y-3 flex-1">
                        <div className="flex items-center gap-3">
                            <Brain className="text-emerald-400" size={24} />
                            <h3 className="text-xl font-black tracking-tight">AI Vector Database Synchronisatie</h3>
                        </div>
                        <p className="text-sm text-slate-300 font-medium leading-relaxed">
                            Zet bestaande kandidaten om in 768-dimensionale wiskundige vectoren. Froukje AI gebruikt deze Semantic Embeddings om <strong className="text-white">ongekend snel</strong> door duizenden CV's te zoeken via de pgvector PostgreSQL engine in plaats van trage API batches.
                        </p>
                    </div>

                    <div className="shrink-0 flex flex-col items-end gap-3 min-w-[280px]">
                        <label className="flex items-center gap-2 text-xs font-bold text-slate-300 cursor-pointer">
                            <input 
                                type="checkbox" 
                                checked={forceRegenerate} 
                                onChange={(e) => setForceRegenerate(e.target.checked)} 
                                className="w-4 h-4 rounded border-slate-700 bg-slate-800 text-emerald-500 focus:ring-emerald-500"
                            />
                            Overschrijf Bestaande Vectoren
                        </label>
                        {forceRegenerate && (
                            <label className="flex items-center gap-2 text-xs font-bold text-slate-300 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={skipEmbeddingsUpdatedToday}
                                    onChange={(e) => setSkipEmbeddingsUpdatedToday(e.target.checked)}
                                    className="w-4 h-4 rounded border-slate-700 bg-slate-800 text-emerald-500 focus:ring-emerald-500"
                                />
                                Sla vandaag al geüpdatete embeddings over (UTC-datum)
                            </label>
                        )}
                        <button
                            onClick={handleGenerateMissingEmbeddings}
                            disabled={isGeneratingEmbeddings}
                            className="w-full px-6 py-4 bg-white text-slate-900 text-sm font-black uppercase tracking-widest rounded-xl hover:bg-emerald-50 focus:ring-4 focus:ring-emerald-500/30 transition-all flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isGeneratingEmbeddings ? (
                                <>
                                    <Loader2 className="animate-spin text-emerald-600" size={20} />
                                    <span>Synchroniseren...</span>
                                </>
                            ) : (
                                <>
                                    <RotateCcw className="text-emerald-600" size={20} />
                                    <span>Vectoren Genereren</span>
                                </>
                            )}
                        </button>

                        {embeddingProgress && (
                            <div className="w-full space-y-2 mt-2">
                                <div className="flex justify-between text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                                    <span>Progress</span>
                                    <span className="text-emerald-400">{embeddingProgress.current} / {embeddingProgress.total}</span>
                                </div>
                                <div className="h-1.5 w-full bg-slate-800 rounded-full overflow-hidden">
                                    <div 
                                        className="h-full bg-emerald-500 transition-all duration-300"
                                        style={{ width: `${(embeddingProgress.current / embeddingProgress.total) * 100}%` }}
                                    ></div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
